import fs from 'node:fs';
import { writeRawOutput } from './brand.js';
import path from 'node:path';
import { getArgValue } from './auth.js';
import type { SnapshotResult, TraceAction } from '@chatsdet/trace-tools';
import { formatSemanticSnapshot } from '@chatsdet/browser-core';

export async function runTrace(argv: string[]): Promise<void> {
  const [subcommand, ...rest] = argv;

  if (subcommand === 'snapshot') {
    await runTraceSnapshot(rest);
    return;
  }

  printTraceHelp();
}

function printTraceHelp(): void {
  console.log(`
Usage: canary trace <subcommand>

Subcommands:
  snapshot <trace-path-or-url>   Reconstruct AI semantic snapshots from a Playwright trace

Options for 'snapshot':
  --output-dir <dir>    Write snapshot files to a directory (default: stdout)
  --screenshots         Include screenshots alongside YAML snapshots
  --html                Include reconstructed DOM HTML alongside YAML snapshots
  --filter <mode>       Which snapshot point per action: before, after, both (default: both)
  --action <index>      Only snapshot a specific action index (0-based)

Examples:
  canary trace snapshot ./trace.zip
  canary trace snapshot https://example.com/trace.zip --output-dir ./replay
  canary trace snapshot ./trace.zip --action 3 --screenshots
`);
}

async function runTraceSnapshot(argv: string[]): Promise<void> {
  const tracePath = argv.find((a) => !a.startsWith('--'));
  if (!tracePath) {
    console.error('Error: trace path or URL is required');
    printTraceHelp();
    process.exit(1);
  }

  const outputDir = getArgValue(argv, '--output-dir');
  const includeScreenshots = argv.includes('--screenshots');
  const includeHtml = argv.includes('--html');
  const filterArg = getArgValue(argv, '--filter') ?? 'both';
  const actionIndexArg = getArgValue(argv, '--action');
  const actionIndex = actionIndexArg !== undefined ? parseInt(actionIndexArg, 10) : undefined;

  if (!['before', 'after', 'both'].includes(filterArg)) {
    console.error(`Error: --filter must be 'before', 'after', or 'both'`);
    process.exit(1);
  }
  const filter = filterArg as 'before' | 'after' | 'both';

  const { reconstructSnapshots, downloadTrace, cleanupTrace } = await import(
    '@chatsdet/trace-tools'
  );

  let localPath = tracePath;
  let isTemp = false;

  if (tracePath.startsWith('http://') || tracePath.startsWith('https://')) {
    console.log('Downloading trace...');
    localPath = await downloadTrace(tracePath);
    isTemp = true;
  }

  try {
    const results = await reconstructSnapshots({
      traceZipPath: localPath,
      includeScreenshots,
      includeHtml,
      filter,
      raw: false,
      actionIndex,
      onProgress: (msg) => process.stderr.write(`${msg}\n`),
    });

    if (outputDir) {
      writeOutputDir(outputDir, results);
      console.log(`Replay written to ${outputDir}/`);
    } else {
      writeStdout(results, localPath);
    }
  } finally {
    if (isTemp) cleanupTrace(localPath);
  }
}

function writeStdout(results: SnapshotResult[], tracePath: string): void {
  const actionCount = new Set(results.map((r) => r.actionIndex)).size;
  const firstTime = results.length > 0 ? results[0]!.action.startTime : 0;

  console.log(`\n${'='.repeat(60)}`);
  console.log(`Agent Replay Log`);
  console.log(`Trace: ${tracePath}`);
  console.log(`Actions: ${actionCount}`);
  console.log(`${'='.repeat(60)}\n`);

  let lastActionIndex = -1;

  for (const result of results) {
    if (result.actionIndex !== lastActionIndex) {
      lastActionIndex = result.actionIndex;
      const action = result.action;
      const relativeMs = action.startTime - firstTime;
      const timeStr = `[t=+${(relativeMs / 1000).toFixed(1)}s]`;
      const errorStr = action.error ? ` ❌ ${action.error.message}` : '';

      console.log(`${'━'.repeat(60)}`);
      console.log(
        `Action ${result.actionIndex + 1}: ${formatActionTitle(action)} ${timeStr}${errorStr}`
      );
      console.log(`${'━'.repeat(60)}\n`);
    }

    const phaseLabel =
      result.phase === 'before'
        ? 'BEFORE (what the agent saw):'
        : 'AFTER (result):';
    console.log(phaseLabel);
    // Opaque captured-application YAML — never branded.
    writeRawOutput(result.yaml);
    console.log('');
  }
}

function snapshotFilename(result: SnapshotResult, ext: string): string {
  const prefix = String(result.actionIndex + 1).padStart(3, '0');
  const slug = slugify(result.action.title);
  return `${prefix}-${slug}-${result.phase}.${ext}`;
}

function writeOutputDir(dir: string, results: SnapshotResult[]): void {
  fs.mkdirSync(dir, { recursive: true });

  let renderedCount = 0;

  for (const result of results) {
    fs.writeFileSync(path.join(dir, snapshotFilename(result, 'yaml')), result.yaml, 'utf-8');

    // Also emit the agent-rendered view (raw YAML piped through the same
    // formatter `browser_snapshot` uses in production). Without this, anyone
    // debugging a workflow run reads the raw .yaml and misses the
    // (1st)/(2nd)/[off-screen]/field: annotations the agent actually saw.
    try {
      const rendered = formatSemanticSnapshot(result.yaml);
      fs.writeFileSync(
        path.join(dir, snapshotFilename(result, 'agent.txt')),
        rendered,
        'utf-8'
      );
      renderedCount += 1;
    } catch (err) {
      // Skip the rendered sibling on formatter failure; raw YAML is still on disk.
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`Skipped agent-rendered snapshot for ${snapshotFilename(result, 'yaml')}: ${msg}`);
    }

    if (result.html) {
      fs.writeFileSync(path.join(dir, snapshotFilename(result, 'html')), result.html, 'utf-8');
    }

    if (result.screenshotBuffer) {
      fs.writeFileSync(path.join(dir, snapshotFilename(result, 'png')), result.screenshotBuffer);
    }
  }

  console.log(
    `Wrote ${results.length} raw snapshot${results.length === 1 ? '' : 's'} (.yaml) ` +
      `and ${renderedCount} agent-rendered (.agent.txt) to ${dir}`
  );

  const replay = {
    actions: results.map((r) => ({
      index: r.actionIndex,
      type: r.action.title,
      params: r.action.params,
      phase: r.phase,
      startTime: r.action.startTime,
      endTime: r.action.endTime,
      error: r.action.error ?? null,
      snapshotFile: snapshotFilename(r, 'yaml'),
    })),
  };
  fs.writeFileSync(path.join(dir, 'replay.json'), JSON.stringify(replay, null, 2), 'utf-8');
}

function formatActionTitle(action: TraceAction): string {
  const { title, params } = action;
  if (!params) return title;
  if (params.selector) return `${title}(${JSON.stringify(params.selector)})`;
  if (params.url) return `${title}(${JSON.stringify(params.url)})`;
  return title;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
}
